import React from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, LogOut, ArrowLeft, MailCheck } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export default function PendingApproval() {
  const { user, bootstrapped, logout } = useAuth();

  if (!bootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass px-6 py-4 fade-up" data-testid="pending-loading">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-blue-500 pulse-soft" />
            <span className="font-mono text-sm text-ink-2">Loading…</span>
          </div>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Already approved — send them into the app.
  if (!user.pending_approval) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="pending-approval-page">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="pill mb-6" data-testid="pending-back-home">
          <span className="dot" /> ← Back to landing
        </Link>

        <div className="glass-strong p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <LogoMark size={44} />
            <div>
              <div className="text-xs text-ink-3 font-medium">BotChainCP</div>
              <div className="font-display text-2xl font-semibold tracking-tight">You&apos;re almost in.</div>
            </div>
          </div>

          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(245,158,11,0.14)" }}>
            <Clock size={22} style={{ color: "#B45309" }} />
          </div>

          <h1 className="font-display text-xl font-semibold tracking-tight mb-2" data-testid="pending-heading">
            Your account is awaiting approval
          </h1>
          <p className="text-sm text-ink-2 leading-relaxed">
            Thanks for signing up{user.name ? `, ${String(user.name).split(" ")[0]}` : ""}. Your sponsor
            (or a team admin) needs to approve your login before you can access the dashboard.
            You&apos;ll get an email the moment it&apos;s active — then just sign back in.
          </p>

          <div className="mt-5 rounded-xl border border-black/5 p-4" style={{ background: "rgba(64,96,255,0.05)" }}>
            <div className="flex items-start gap-2.5">
              <MailCheck size={16} style={{ color: "#4060FF" }} className="mt-0.5 shrink-0" />
              <div className="text-xs text-ink-2">
                <div className="font-medium text-ink-1 mb-0.5">While you wait</div>
                You can keep watching the intro video and work through the onboarding steps — nothing you enter there is lost.
              </div>
            </div>
          </div>

          <p className="text-xs text-ink-3 mt-5" data-testid="pending-signed-in-as">
            Signed in as <b className="text-ink-2">{user.email}</b>
          </p>

          <button
            type="button"
            onClick={logout}
            className="btn-secondary w-full justify-center mt-4 inline-flex items-center gap-2"
            data-testid="pending-logout"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
